import "./SearchPage.less";

import { Spin } from "antd";
import * as QueryString from "query-string";
import * as React from "react";
import { RouteComponentProps } from "react-router-dom";

import { SectionApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { SearchApi } from "lib/api/search";
import { IBaseProps } from "lib/data/interfaces";
import { Entry, Section } from "lib/data/model";
import { SearchItem, SearchRequest } from "lib/data/transfer";
import { ComponentService, StateService } from "lib/services";

import { GradientBar, SearchBar } from "components/general";


interface ISearchPageProps extends IBaseProps, RouteComponentProps<any> {

}

interface ISearchPageState {
	isSearching: boolean;
}


export class SearchPage extends React.Component<ISearchPageProps, ISearchPageState> {

	public static readonly BasePath: string = "/search";


	private apiCancellationToken: CancellationToken = new CancellationToken();

	private searchQuery: string | undefined;
	private searchResults: SearchItem[] = [];

	private searchResultsComponents: Array<React.ReactElement<GradientBar>> = [];


	constructor(props: ISearchPageProps) {

		super(props);

		this.state = {
			isSearching: true
		};
	}

	public async componentWillMount() {

		// Get Search Query
		const searchQuery = QueryString.parse(this.props.location.search).q;

		// Check If Query Returns Multiple
		if (Array.isArray(searchQuery)) {

			// Concatenate Search Queries
			this.searchQuery = searchQuery.join(" ");
		}
		else if (searchQuery) {

			// Store Search Query As-Is
			this.searchQuery = searchQuery;
		}
		else {

			// Store Undefined Search Query
			this.searchQuery = undefined;
		}

		// Perform Search
		await this.performSearch(this.searchQuery);

		// Indicate Search Performed
		StateService.setCancelableState(() => this.setState({ isSearching: false }), this.apiCancellationToken);
	}

	public async componentWillReceiveProps(newProps: ISearchPageProps) {

		// Indicate Is Search
		StateService.setCancelableState(() => this.setState({ isSearching: true }), this.apiCancellationToken);

		// Get Search Query
		const searchQuery = QueryString.parse(newProps.location.search).q;

		// Check If Query Returns Multiple
		if (Array.isArray(searchQuery)) {

			// Concatenate Search Queries
			this.searchQuery = searchQuery.join(" ");
		}
		else if (searchQuery) {

			// Store Search Query As-Is
			this.searchQuery = searchQuery;
		}
		else {

			// Store Undefined Search Query
			this.searchQuery = undefined;
		}

		// Perform Search
		await this.performSearch(this.searchQuery);

		// Indicate Is NOT Searching
		StateService.setCancelableState(() => this.setState({ isSearching: false }), this.apiCancellationToken);
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();
	}

	public render() {

		return (
			<div className="search-page">

				{/* <header className="introduction">

					<h3>Government Directory</h3>

					<div className="spacer-10-h" />

					<SearchBar searchString={this.searchQuery} onSearch={this.onDirectorySearched} placeholder="What are you looking for today?" />

					<div className="spacer-10-h" />

				</header> */}

				<section className="page-content">

					<section className="search-page-results">

						{(this.state.isSearching
							?
							(
								<React.Fragment>
									<GradientBar
										className="search-page-results-header"
										title="Searching..."
										size="large"
									/>
									<Spin />
								</React.Fragment>
							)
							:
							(
								<React.Fragment>
									<GradientBar
										className="search-page-results-header"
										title="Search Results"
										size="large"
									>
										<p>
											These results may contain two types of results applicable to your search. The first type may be an entity such as an agency,
											county, judicial office, etc. The second type may be an individual which links to a page listing all entities to which that individual is associated.
										</p>
										<span>{`Results: ${this.searchResults.length}`}</span>
									</GradientBar>

									{/* Result Sections */}
									<section className="search-page-results-content">

										{this.searchResultsComponents}

									</section>
								</React.Fragment>
							)
						)}
					</section>
				</section>
			</div>
		);
	}

	private performSearch = async (searchString?: string) => {

		// Check For Search Query
		if (searchString) {

			const requests: SearchRequest = {
				objects: [
					"Section",
					"Entry"
				],
				searchPhrase: searchString
			};

			// Search Sections
			this.searchResults = await SearchApi.search(requests);

			// Loop Through Results
			this.searchResultsComponents = await this.buildResultsComponents(this.searchResults);
		}
	}

	private buildResultsComponents = async (searchResults: SearchItem[]): Promise<Array<React.ReactElement<GradientBar>>> => {

		// Initialize Search Result Components
		const components: Array<React.ReactElement<GradientBar>> = [];

		// Loop Through Search Results
		for (let i = 0; i < searchResults.length; i++) {

			// Build Click Handler
			const clickHandler = () => this.getOnClickHandler(searchResults[i]);

			// Check Search Item Type
			switch (searchResults[i].item.$type) {

				case Section.$type:

					// Get Section Hierarchy
					const sectionHierarchy = await SectionApi.getHierarchy((searchResults[i].item as Section).id);

					// Add Section Component
					components.push(
						<GradientBar
							key={i}
							className={this.getResultClassName(searchResults[i])}
							size="medium"
							title={(searchResults[i].item as Section).published!.name}
							onClick={clickHandler}
							superTitle={ComponentService.getHierarchyString(sectionHierarchy)}
						/>
					);

					// Exit Switch
					break;

				case Entry.$type:

					// Add Entry Component
					components.push(
						<GradientBar
							key={i}
							className={this.getResultClassName(searchResults[i])}
							size="medium"
							title={(searchResults[i].item as Entry).name}
							onClick={clickHandler}
						/>
					);

					// Exit Switch
					break;
			}
		}

		// Return Components
		return components;
	}

	private onDirectorySearched = (searchString: string) => {

		// Redirect To Current Page With New Parameters
		this.props.history.push(`${SearchPage.BasePath}?q=${searchString}`);
	}

	private getResultClassName = (searchItem: SearchItem): string => {

		// Initialize Class Name
		let className = "search-page-results-component";

		if (searchItem.item.$type === Section.$type) {

			// Append Additional Class Name
			className += " section-component";
		}
		else if (searchItem.item.$type === Entry.$type) {

			// Append Additional Class Name
			className += " entry-component";
		}

		// Return Class Name
		return className;
	}

	private getOnClickHandler = (searchItem: SearchItem) => {

		// Store Item ID
		const id = searchItem.item.id;

		// Check Type
		if (id && searchItem.item.$type === Section.$type) {

			// Redirect To New Section
			this.props.history.push(`/section/${id}?q=${this.searchQuery}`);
		}
		else if (id && searchItem.item.$type === Entry.$type) {

			// Redirect to New Entry
			this.props.history.push(`/entry/${id}?q=${this.searchQuery}`);
		}

		// Return Nothing
		return undefined;
	}
}
